/* Trust page copy (English).
 *
 * Organised around the brand's four "Pri" words (the hero typewriter cycle):
 * Principles, Privacy, Prime, Priority. Each is a pillar: a teal "Pri" title +
 * a plain subtitle that carries the real meaning (clarity over cleverness),
 * a summary, and collapsed detail (progressive disclosure). Then an FAQ and
 * legal links.
 *
 * Every claim is cross-checked against Privacy/Terms/Sub-processors so the
 * pages never contradict each other:
 *  - Data is STORED in the Kingdom (Riyadh live, Jeddah backups) but meeting
 *    content IS sent to AI providers abroad to transcribe/summarize -> stated
 *    openly (Privacy S6 / S11).
 *  - Backups: encrypted, keys held apart, purged within 30 days (Privacy
 *    S12 / S15). The 21-day immutability is added detail, not a conflict.
 *  - AI-training claim scoped to "our AI providers" (Privacy S6).
 *  - Audio is deleted from OUR systems as soon as processing finishes (shipped
 *    2026-07-28). Retention is an opt-in paid-plan feature. The claim must stay
 *    scoped to our systems: when Drive/OneDrive is connected, audio is
 *    auto-uploaded to the user's own storage FIRST and survives our deletion.
 */

export type TrustIcon = 'shield-check' | 'no-ai' | 'lock' | 'star' | 'pin' | 'doc';

export interface TrustBadge {
  icon: TrustIcon;
  title: string;
  line: string;
}

export interface TrustPoint {
  label: string;
  body: string;
}

export interface PointGroup {
  subhead?: string;
  points: TrustPoint[];
}

export interface TrustPillar {
  id: string;
  icon: TrustIcon;
  word: string;
  /* Leading segment of `word` rendered teal. English pillars all share the
   * "Pri" prefix, which is the whole conceit of the page. Arabic has no such
   * acrostic (the four words are translations: مبادئ / خصوصية / امتياز /
   * أولوية), so AR omits this and the word renders in a single colour. Stated
   * per pillar rather than sliced blindly at 3 characters, which silently
   * coloured three arbitrary letters in any non-English locale. */
  pri?: string;
  subtitle: string;
  summary: string;
  detailLabel: string;
  groups: PointGroup[];
  linkTo?: string;
  linkLabel?: string;
}

export interface TrustFaq {
  q: string;
  a: string;
}

export interface TrustDoc {
  eyebrow: string;
  title: string;
  lead: string;

  /* aria-labels on landmark regions; hardcoded English in the component
   * before Arabic existed. (The document <title> now lives in copy/meta-*.ts,
   * owned by useDocumentHead.) */
  a11y: { reg: string; badges: string };

  /* The brand token inside `pillarsTitle`, split so the "PRI" part renders
   * teal. Data-driven because the Arabic brand is برَينوتس, which no search
   * for the literal "PRINOTES" would ever find. The two halves must
   * concatenate to a substring of `pillarsTitle`. */
  brand: { pri: string; rest: string };

  reg: {
    heading: string;
    regLabel: string;
    regNo: string;
    statusLabel: string;
    status: string;
    issuedLabel: string;
    issued: string;
    platform: string;
    verifyUrl: string;
    verifyLabel: string;
    qrCaption: string;
    logoAlt: { sdaia: string; ndgp: string };
  };

  badges: TrustBadge[];

  pillarsTitle: string;
  pillars: TrustPillar[];

  faqTitle: string;
  faq: TrustFaq[];

  relatedTitle: string;
  related: { to: string; label: string }[];
}

export const TRUST_EN: TrustDoc = {
  eyebrow: 'Trust & security',
  title: 'Your data is yours.',
  lead: 'Your conversations, meetings, and data stay yours. Prinotes is registered with Saudi Arabia’s National Register for Personal Data Protection, stores your data within the Kingdom, and never uses it to train AI models.',

  a11y: { reg: 'SDAIA registration', badges: 'Certifications and compliance' },
  brand: { pri: 'PRI', rest: 'NOTES' },

  reg: {
    heading: 'Registered with Saudi Arabia’s National Register for Personal Data Protection',
    regLabel: 'Registration No.',
    regNo: '3260007171',
    statusLabel: 'Status',
    status: 'Active',
    issuedLabel: 'Issued',
    issued: '18 July 2026',
    platform:
      'Through the National Data Governance Platform (NDGP), operated by the Saudi Data & Artificial Intelligence Authority (SDAIA).',
    verifyUrl:
      'https://dgp.sdaia.gov.sa/wps/portal/pdp/services/certificate/ee68dcfb-d682-f111-b128-005056ab563b/!ut/p/z1/jY_RCoIwFIafxQcYO9vYWJcWQlqLQjTbTWzpTCiVkC56-kaXSdq5--H7f86HNS6wbs2zqc3QdK25-XzS4hyHkViTFCjJlxwEUzvON4xCxPDxC8jyhQcOVCVhzGDPsP6nDz8uhLl-Mgd4A_pQK1Vj3ZvhiprWdbioKiHLi7OoFJIiRwhBllCJADhwYSwXzPrf9OS6pCNgrP8BJvz6e1a8ti6N6yB4A6PjMao!/',
    verifyLabel: 'Verify with SDAIA',
    qrCaption: 'Scan the QR code to verify',
    logoAlt: {
      sdaia: 'Saudi Data & Artificial Intelligence Authority (SDAIA)',
      ndgp: 'National Data Governance Platform',
    },
  },

  badges: [
    {
      icon: 'shield-check',
      title: 'Registered with SDAIA',
      line: 'Registered in Saudi Arabia’s National Register for Personal Data Protection, No. 3260007171.',
    },
    {
      icon: 'pin',
      title: 'Hosted in the Kingdom',
      line: 'Your data is stored and backed up in Saudi Arabia.',
    },
    {
      icon: 'doc',
      title: 'PDPL compliant',
      line: 'Built to comply with Saudi Arabia’s Personal Data Protection Law and its Implementing Regulations.',
    },
    {
      icon: 'lock',
      title: 'Enterprise-grade infrastructure',
      line: 'Runs on Oracle Cloud Infrastructure, which is independently certified for ISO 27001 and SOC 2.',
    },
  ],

  pillarsTitle: 'The pillars of trust at PRINOTES',
  pillars: [
    {
      id: 'principles',
      icon: 'shield-check',
      word: 'Principles',
      pri: 'Pri',
      subtitle: 'Your data is yours.',
      summary:
        'We built Prinotes on one belief: your conversations, meetings, and data belong to you, not us. We keep your data only to operate the service, never to use it for our own purposes. Everything we do with your data is transparent and something you can verify yourself.',
      detailLabel: 'Our commitments',
      groups: [
        {
          points: [
            {
              label: 'You own your meetings.',
              body: 'Your conversations and meetings, with their recordings, minutes, and notes, are yours. We are the custodian, not the owner, and you decide what happens to them.',
            },
            {
              label: 'Accountable, not just self-declared.',
              body: 'Prinotes is operated by Priyora Company, registered with SDAIA’s National Register for Personal Data Protection (No. 3260007171, Active), and built to comply with Saudi Arabia’s PDPL. Scan the QR code above to verify it yourself.',
            },
            {
              label: 'Transparent by default.',
              body: 'We disclose every provider that helps run Prinotes, what it does, and where your data goes. Nothing about how we handle your data is hidden.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'Read our Privacy Policy',
    },
    {
      id: 'privacy',
      icon: 'no-ai',
      word: 'Privacy',
      pri: 'Pri',
      subtitle: 'Never sold. Never used to train AI.',
      summary:
        'We never sell your data, use it for advertising, or train AI models on it. Your Google and Microsoft data is read in real time only for the features you request and is never copied.',
      detailLabel: 'See what this means in practice',
      groups: [
        {
          points: [
            {
              label: 'Your data is never used to train AI.',
              body: 'Your conversation and meeting content is never used to train AI models, not by us and not by our AI providers. We use paid services that do not train their models on customer content.',
            },
            {
              label: 'Never sold. Never used for advertising.',
              body: 'We do not sell your data or use your conversation and meeting content for advertising or to create profiles of you.',
            },
            {
              label: 'Read in real time, never copied.',
              body: 'Your contacts, calendar, tasks, and files are read in real time only for the features you request and are never stored on our servers.',
            },
            {
              label: 'Sent for transcription, never for training.',
              body: 'To transcribe and summarize, your meeting content is sent to our AI providers (OpenAI and Google Gemini) outside the Kingdom, through paid tiers that do not train their models on it and delete it within about 30 days. All other data stays inside Saudi Arabia.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'Read our Privacy Policy',
    },
    {
      id: 'prime',
      icon: 'lock',
      word: 'Prime',
      pri: 'Pri',
      subtitle: 'Encrypted, in-Kingdom, disaster-proof.',
      summary:
        'Your meetings are encrypted in transit and at rest, stored inside Saudi Arabia on Oracle Cloud Infrastructure, and backed up for disaster recovery.',
      detailLabel: 'See the security details',
      groups: [
        {
          subhead: 'Encryption & hosting',
          points: [
            {
              label: 'Encrypted in transit.',
              body: 'Every connection uses HTTPS with modern TLS versions (1.2 and 1.3). Older and weaker protocols are disabled, and you can verify our configuration with any public SSL testing tool.',
            },
            {
              label: 'Encrypted at rest.',
              body: 'Your data is encrypted at rest using AES-256 through Oracle Cloud Infrastructure. Connected-account tokens receive an additional layer of encryption at the application level.',
            },
            {
              label: 'Stored in Saudi Arabia.',
              body: 'Your account, conversation, and meeting data remain on infrastructure inside the Kingdom: primary data in Riyadh, with encrypted backups in Jeddah.',
            },
            {
              label: 'Enterprise-grade infrastructure.',
              body: 'Prinotes runs on Oracle Cloud Infrastructure, which holds ISO 27001 and SOC 2 certifications for its data centers.',
            },
          ],
        },
        {
          subhead: 'Backups built for disaster recovery',
          points: [
            {
              label: 'Daily, encrypted, with keys stored separately.',
              body: 'Each backup is encrypted before leaving the server, and its decryption key is never stored on the systems that create it. Access to our servers does not grant access to the backup contents.',
            },
            {
              label: 'A second region, also within the Kingdom.',
              body: 'Backups are written to Jeddah, a separate Oracle Cloud region about 850 km from your primary data in Riyadh, with independent power and network infrastructure. This geographic separation helps isolate risks such as fire, flood, or regional outages at one site from the other. Both are inside Saudi Arabia.',
            },
            {
              label: 'Immutable, so no one can destroy them.',
              body: 'For the first 21 days, backups cannot be altered or deleted by anyone: not an attacker, not our staff, and not even us. Even in the event of a full compromise, your meetings remain recoverable.',
            },
          ],
        },
      ],
      linkTo: '/subprocessors',
      linkLabel: 'See who processes your data',
    },
    {
      id: 'priority',
      icon: 'star',
      word: 'Priority',
      pri: 'Pri',
      subtitle: 'You come first, and stay in control.',
      summary:
        'You decide what Prinotes keeps: delete any meeting or your entire account at any time, your rights are protected, and if anything ever goes wrong, we put your protection first.',
      detailLabel: 'See how you stay in control',
      groups: [
        {
          points: [
            {
              label: 'You stay in control.',
              body: 'Your audio is deleted from our systems after processing is complete. Only the transcript and notes are kept until you delete them. We retain audio only if you enable audio retention, a feature available on paid plans for the period included in your plan. If you connect Drive, your own copy remains in your account. When you delete a meeting or your account, it is removed from our operational systems immediately and permanently deleted from our encrypted backups within 30 days.',
            },
            {
              label: 'Your rights are protected.',
              body: 'You can access, correct, or delete your data at any time, and we respond to your requests in line with the Saudi PDPL.',
            },
            {
              label: 'If something ever goes wrong.',
              body: 'If a data breach may affect you, we notify SDAIA within 72 hours and inform you without undue delay in clear language.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'Read our Privacy Policy',
    },
  ],

  faqTitle: 'Frequently asked questions',
  faq: [
    {
      q: 'How does Prinotes create meeting minutes?',
      a: 'You record a meeting live or upload an existing recording. Prinotes transcribes it, then generates a summary, key decisions, and action items that you can review, edit, and share.',
    },
    {
      q: 'Is Prinotes secure and compliant with Saudi Arabia’s Personal Data Protection Law?',
      a: 'Yes. Prinotes is operated by Priyora Company, registered with SDAIA’s National Register for Personal Data Protection (No. 3260007171), and built in accordance with the Saudi Personal Data Protection Law. Your data is stored within Saudi Arabia and encrypted both in transit and at rest.',
    },
    {
      q: 'Can I use Prinotes for confidential meetings?',
      a: 'Yes. Meeting content is encrypted in transit and at rest, and your data is stored within Saudi Arabia and handled in accordance with the Saudi Personal Data Protection Law. Sign-in is passwordless through your Google or Microsoft account, so there is no Prinotes password for attackers to steal. For transcription and summarization, content is processed by our AI providers on paid tiers that do not train on customer content. Your meetings remain private to you and the people you choose to share them with.',
    },
    {
      q: 'Does Prinotes send my conversations or meetings to any third parties?',
      a: 'Two places only: our AI providers, to transcribe and summarize (never to train, and deleted within about 30 days), and your own cloud storage, if you connect it. If you grant Prinotes access to Google Drive or OneDrive, it automatically saves each meeting’s recording and transcript to your account, so you always keep a copy under your control. You can decline this permission or turn off automatic saving at any time. Because these files are stored in your account, deleting a meeting in Prinotes does not remove them from Drive or OneDrive; you delete them there yourself.',
    },
    {
      q: 'Are my conversations, meetings, or data used to train AI models?',
      a: 'No. Your data is never used to train AI models, not ours and not our providers’. We use paid AI tiers that do not train on customer content.',
    },
    {
      q: 'Who can access my meetings and their data?',
      a: 'Your meetings remain private to you and the people you choose to share them with.',
    },
    {
      q: 'Where is my data stored?',
      a: 'Your data is stored in Saudi Arabia: primary data in Riyadh and encrypted backups in Jeddah, both hosted on Oracle Cloud Infrastructure within the Kingdom.',
    },
  ],

  relatedTitle: 'Read the details',
  related: [
    { to: '/privacy', label: 'Privacy Policy' },
    { to: '/terms', label: 'Terms of Service' },
    { to: '/subprocessors', label: 'Sub-processors' },
  ],
};
